import { useEffect, useState } from "react";
import Cookies from "js-cookie";

import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { ListItemText, MenuItem, Select, Typography, Box, Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import { FileDownload as FileDownloadIcon } from "@mui/icons-material";
import { toast } from "react-toastify";
import moment from "moment";

import { getCurrentUserSelector } from "../../../redux/users/selector";
import { getJobByIdSelector } from "../../../redux/jobs/selector";
import CustomTabs from "../../../components/shared/Tabs";
import JobForm from "../../../components/shared/JobForm";
import { JobStatus, Roles } from "../../../utilities/constants";
import Applications from "../../private/Applications";
import PrimaryButton from "../../../components/shared/PrimaryButton";
import SecondaryButton from "../../../components/shared/SecondaryButton";
import { getJobById, updateJob, deleteJob } from "../../../redux/jobs/actions";
import { getApplications } from "../../../redux/application/actions";
import { getApplicationsForJob } from "../../../redux/application/selectors";
import store from "../../../redux/store";
import View from "./components/View";

const TabPanel = ({ children, value, index, ...other }) => (
  <div role="tabpanel" hidden={value !== index} id={`simple-tabpanel-${index}`} aria-labelledby={`simple-tab-${index}`} {...other}>
    {value === index && (
      <Box sx={{ p: 3 }}>
        <Typography>{children}</Typography>
      </Box>
    )}
  </div>
);

TabPanel.propTypes = {
  children: PropTypes.node.isRequired,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

// Delete Confirmation Popup Component
const DeleteConfirmationPopup = ({ open, onClose, onConfirm, onUndo, jobTitle, countdown }) => (
  <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
    <DialogTitle sx={{ color: "error.main", fontWeight: "bold" }}>Delete Job Confirmation</DialogTitle>
    <DialogContent>
      <Typography variant="body1" sx={{ mb: 2 }}>
        Are you sure you want to delete <strong>{jobTitle}</strong>?
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Job will be deleted in {countdown} seconds. Click &quot;Undo&quot; to cancel.
      </Typography>
    </DialogContent>
    <DialogActions sx={{ justifyContent: "space-between", px: 3, pb: 2 }}>
      <SecondaryButton onClick={onUndo} color="primary">
        Undo
      </SecondaryButton>
      <PrimaryButton onClick={onConfirm} color="error" variant="contained">
        Delete Now
      </PrimaryButton>
    </DialogActions>
  </Dialog>
);

// Add PropTypes validation
DeleteConfirmationPopup.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  onUndo: PropTypes.func.isRequired,
  jobTitle: PropTypes.string.isRequired,
  countdown: PropTypes.number.isRequired,
};

const SingleJob = () => {
  const dispatch = useDispatch();
  const params = useParams();
  const navigate = useNavigate();

  const job = useSelector(getJobByIdSelector(params.id));
  const user = useSelector(getCurrentUserSelector);
  const previousPath = useSelector((state) => state.utilities.previousPath);
  const [deleteConfirmation, setDeleteConfirmation] = useState({
    open: false,
    jobTitle: "",
    jobId: null,
    countdown: 20,
    timer: null,
    interval: null,
  });

  useEffect(() => {
    if (!job) {
      dispatch(getJobById({ id: params.id }))
        .unwrap()
        .catch((err) => {
          if (err?.status === 404) {
            const isFromDashboard = Cookies.get("isFromDashboard");

            Cookies.remove("isFromDashboard");

            navigate(isFromDashboard ? "/dashboard" : "/jobs");
          }
        });
    }
  }, [job, params.id, dispatch, navigate]);

  /* eslint-disable arrow-body-style */
  useEffect(() => {
    return () => {
      if (deleteConfirmation.timer) clearTimeout(deleteConfirmation.timer);
      if (deleteConfirmation.interval) clearInterval(deleteConfirmation.interval);
    };
  }, []);
  /* eslint-enable arrow-body-style */

  const handleDeleteJob = () => {
    const jobTitle = job?.title || "this job";

    // Clear any existing timers
    if (deleteConfirmation.timer) clearTimeout(deleteConfirmation.timer);
    if (deleteConfirmation.interval) clearInterval(deleteConfirmation.interval);

    setDeleteConfirmation({
      open: true,
      jobTitle,
      jobId: job.id,
      countdown: 20,
      timer: null,
      interval: null,
    });

    // Start countdown
    const interval = setInterval(() => {
      setDeleteConfirmation((prev) => {
        if (prev.countdown <= 1) {
          clearInterval(interval);
          return prev;
        }
        return { ...prev, countdown: prev.countdown - 1 };
      });
    }, 1000);

    // Auto-delete after 20 seconds
    const timer = setTimeout(() => {
      clearInterval(interval);
      dispatch(deleteJob({ id: job.id }))
        .unwrap()
        .then(() => {
          toast.success("The job has been deleted.");
          const isFromDashboard = Cookies.get("isFromDashboard");

          Cookies.remove("isFromDashboard");

          navigate(isFromDashboard ? "/dashboard" : previousPath || "/jobs");
        });
      setDeleteConfirmation((prev) => ({ ...prev, open: false }));
    }, 20000);

    // Store timer references
    setDeleteConfirmation((prev) => ({
      ...prev,
      timer,
      interval,
    }));
  };

  const handleConfirmDelete = () => {
    // Clear timers
    if (deleteConfirmation.timer) clearTimeout(deleteConfirmation.timer);
    if (deleteConfirmation.interval) clearInterval(deleteConfirmation.interval);

    // Delete immediately
    dispatch(deleteJob({ id: deleteConfirmation.jobId }))
      .unwrap()
      .then(() => {
        toast.success("The job has been deleted.");
        const isFromDashboard = Cookies.get("isFromDashboard");

        Cookies.remove("isFromDashboard");

        navigate(isFromDashboard ? "/dashboard" : previousPath || "/jobs");
      });
    setDeleteConfirmation((prev) => ({ ...prev, open: false }));
  };

  const handleUndoDelete = () => {
    // Clear timers to cancel the deletion
    if (deleteConfirmation.timer) clearTimeout(deleteConfirmation.timer);
    if (deleteConfirmation.interval) clearInterval(deleteConfirmation.interval);

    setDeleteConfirmation((prev) => ({ ...prev, open: false }));
  };

  const handleExportApplicants = async () => {
    try {
      // Fetch applications if not already loaded
      await dispatch(getApplications({ jobId: job.id })).unwrap();

      // Get applications from Redux state
      const state = store.getState();
      const currentApplications = getApplicationsForJob(state)(job.id) || [];

      if (!currentApplications || currentApplications.length === 0) {
        toast.info("No applicants to export.");
        return;
      }

      // Prepare CSV data
      const csvContent = [
        [`Applicants for the ${job.title || "Job"}`],
        [`Total applicants for this job ${currentApplications.length}`],
        ["#", "User Email", "Application Date"],
        ...currentApplications.map((app, index) => [index + 1, app.user?.email || "", app.createdAt ? moment(app.createdAt).format("DD/MM/YYYY hh:mm") : ""]),
      ]
        .map((e) => e.map((field) => `"${String(field).replace(/"/g, '""')}"`).join(","))
        .join("\n");

      // Create and download CSV file
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      const fileName = `applicants_${(job.title || job.id).toString().replace(/[^a-z0-9]/gi, "_")}_${moment().format("YYYY-MM-DD")}.csv`;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success("Applicants exported successfully.");
    } catch (error) {
      toast.error("Failed to export applicants.");
    }
  };

  return (
    job &&
    (user === null || user?.role === Roles.USER.value ? (
      <View job={job} />
    ) : (
      <>
        <CustomTabs
          tabs={[
            {
              label: "View",
              value: "view",
              component: <View job={job} />,
              onClick: () => navigate("?tab=view"),
            },
            {
              label: "Edit",
              value: "edit",
              component: <JobForm job={job} />,
              onClick: () => navigate("?tab=edit"),
              disabled: job?.status === JobStatus.CLOSED.value,
            },
            {
              label: "Applicants",
              value: "applicants",
              component: <Applications jobId={job.id} />,
              onClick: () => navigate("?tab=applicants"),
            },
            {
              label: "Assign",
              value: "assign",
              component: <Box>Assign</Box>,
              onClick: () => {
                Cookies.set("applyFilter", '{"isEmailVerified": true, "isAccountVerified": true}', { expires: 1 });
                navigate("/users", {
                  state: {
                    assignJob: {
                      id: job.id,
                      title: job.title,
                    },
                  },
                });
              },
            },
          ]}
          header={
            <Box pb={1}>
              {[Roles.ADMIN.value, Roles.COMPANY.value].includes(user?.role) && (
                <>
                  <PrimaryButton onClick={handleExportApplicants} sx={{ marginRight: "20px" }}>
                    <FileDownloadIcon />
                  </PrimaryButton>
                  <PrimaryButton onClick={handleDeleteJob} sx={{ marginRight: "20px" }}>
                    Delete
                  </PrimaryButton>
                </>
              )}
              <PrimaryButton
                onClick={() => {
                  const isFromDashboard = Cookies.get("isFromDashboard");

                  Cookies.remove("isFromDashboard");

                  navigate(isFromDashboard ? "/dashboard" : previousPath || "/jobs");
                }}
                sx={{ marginRight: "20px" }}>
                Back
              </PrimaryButton>
              <Select sx={{ height: "45px" }} disabled={job.status === JobStatus.CLOSED.value} value={job.status} onChange={(e) => dispatch(updateJob({ ...job, status: e.target.value }))}>
                {Object.values(JobStatus).map((val) => (
                  <MenuItem key={val.value} value={val.value} selected={val.value === job.status}>
                    <ListItemText primary={val?.label} />
                  </MenuItem>
                ))}
              </Select>
            </Box>
          }
          defaultTab="view"
        />
        <DeleteConfirmationPopup
          open={deleteConfirmation.open}
          onClose={() => setDeleteConfirmation((prev) => ({ ...prev, open: false }))}
          onConfirm={handleConfirmDelete}
          onUndo={handleUndoDelete}
          jobTitle={deleteConfirmation.jobTitle}
          countdown={deleteConfirmation.countdown}
        />
      </>
    ))
  );
};

export default SingleJob;
